'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  Address,
  AddressType,
  Capacity,
  Complaint,
  ComplaintOverview,
  ComplaintStatusFilter,
  Contact,
  DeliveryNotice,
  Employee,
  Order,
  SessionInfo,
  User,
} from '@/types/service-api';

import { OVERRIDE_HEADER } from './constants';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseApiReturn<T> extends UseApiState<T> {
  refetch: () => Promise<void>;
}

// Generic hook for API calls
function useApi<T>(
  fetcher: () => Promise<Response>,
  immediate = true
): UseApiReturn<T> {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: immediate,
    error: null,
  });

  // Store fetcher in a ref to avoid re-creating fetchData on every render
  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  const fetchData = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await fetcherRef.current();
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Request failed');
      }

      setState({ data, loading: false, error: null });
    } catch (error) {
      setState({
        data: null,
        loading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }, []);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  }, [fetchData, immediate]);

  return {
    ...state,
    refetch: fetchData,
  };
}

// Hook for mutations (POST, PUT, DELETE)
interface UseMutationReturn<T, V> {
  mutate: (variables: V) => Promise<void>;
  data: T | null;
  loading: boolean;
  error: string | null;
  reset: () => void;
}

function useMutation<T, V>(
  mutationFn: (variables: V) => Promise<Response>
): UseMutationReturn<T, V> {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  // Store mutationFn in a ref to avoid re-creating mutate on every render
  const mutationFnRef = useRef(mutationFn);
  useEffect(() => {
    mutationFnRef.current = mutationFn;
  }, [mutationFn]);

  const mutate = useCallback(async (variables: V) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await mutationFnRef.current(variables);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Request failed');
      }

      setState({ data, loading: false, error: null });
    } catch (error) {
      setState({
        data: null,
        loading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return {
    mutate,
    ...state,
    reset,
  };
}

// Optional mail override (test-page only). When set, requests carry the
// `x-service-mail-override` header so the service-api routes resolve account
// data for this mail instead of the logged-in session user. The backend route
// honors it only outside production. Defaults to null (no override) so all
// production hook consumers behave exactly as before.
let mailOverride: string | null = null;

export function setServiceMailOverride(mail: string | null): void {
  mailOverride = mail && mail.trim() ? mail.trim() : null;
}

function serviceFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  if (!mailOverride) {
    return fetch(input, init);
  }
  const headers = new Headers(init?.headers);
  headers.set(OVERRIDE_HEADER, mailOverride);
  return fetch(input, { ...init, headers });
}

// Address Hooks
export function useAddress(customerNr: number, enabled = true) {
  return useApi<Address>(
    () => serviceFetch(`/api/service/address/${customerNr}`),
    enabled
  );
}

export function useAddressByOrder(
  orderNr: number,
  orderSubNr: number,
  addressType: AddressType,
  enabled = true
) {
  return useApi<Address>(
    () =>
      serviceFetch(
        `/api/service/address/${orderNr}/${orderSubNr}?addressType=${addressType}`
      ),
    enabled
  );
}

// User Hook
export function useUser(enabled = true) {
  return useApi<User>(() => serviceFetch('/api/service/user'), enabled);
}

// Capacity Hook
export function useCapacity(enabled = true) {
  return useApi<Capacity>(() => serviceFetch('/api/service/capacity'), enabled);
}

// Contact Hooks
export function useContacts(customerNr: string, enabled = true) {
  return useApi<Contact[]>(
    () => serviceFetch(`/api/service/contact/list?customerNr=${customerNr}`),
    enabled
  );
}

// Complaint Hooks
export function useComplaintOverview(complaintId: number, enabled = true) {
  return useApi<ComplaintOverview>(
    () => serviceFetch(`/api/service/complaint/${complaintId}/overview`),
    enabled
  );
}

export function useComplaints(
  customerNr: string,
  complaintStatus: ComplaintStatusFilter = 'NotCompleted',
  enabled = true
) {
  return useApi<Complaint[]>(
    () =>
      serviceFetch(
        `/api/service/complaint/list?customerNr=${customerNr}&complaintStatus=${complaintStatus}`
      ),
    enabled
  );
}

export function useCreateComplaint() {
  return useMutation<Complaint, Complaint>((complaint) =>
    serviceFetch('/api/service/complaint', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(complaint),
    })
  );
}

// Order Hook
export function useOrder(orderNr: number, orderSubNr: number, enabled = true) {
  return useApi<Order>(
    () => serviceFetch(`/api/service/order/${orderNr}/${orderSubNr}`),
    enabled
  );
}

// Session Hook
export function useSessionInfo(enabled = true) {
  return useApi<SessionInfo>(() => fetch('/api/service/session'), enabled);
}

// Customer Number Hook - convenience hook to get just the customer number
export function useCustomerNumber(enabled = true) {
  const { data, loading, error, refetch } = useSessionInfo(enabled);
  return {
    customerNumber: data?.customerNumber || null,
    loading,
    error,
    refetch,
  };
}

// Delivery Notice Hook
export function useDeliveryNotices(customerNr: number | null, enabled = true) {
  return useApi<DeliveryNotice[]>(
    () => serviceFetch(`/api/service/deliveryNotice/${customerNr}`),
    enabled && customerNr !== null
  );
}

// Employee Presence Hook
export function useEmployeePresence(employeeShort: string | null, enabled = true) {
  return useApi<Employee>(
    () => serviceFetch(`/api/service/employee/${employeeShort}/presence`),
    enabled && employeeShort !== null
  );
}

// File Upload Hooks
interface UseFileUploadReturn {
  upload: (complaintId: number, file: File, customerNr: string) => Promise<boolean>;
  loading: boolean;
  error: string | null;
  progress: number;
  reset: () => void;
}

export function useUploadComplaintFile(): UseFileUploadReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setProgress(0);
  }, []);

  const upload = useCallback(
    async (
      complaintId: number,
      file: File,
      customerNr: string
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);
      setProgress(0);

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('customerNr', customerNr);

        // Use XMLHttpRequest for progress tracking
        return new Promise((resolve) => {
          const xhr = new XMLHttpRequest();

          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const percentComplete = Math.round((event.loaded / event.total) * 100);
              setProgress(percentComplete);
            }
          };

          xhr.onload = () => {
            setLoading(false);
            if (xhr.status >= 200 && xhr.status < 300) {
              setProgress(100);
              resolve(true);
            } else {
              try {
                const response = JSON.parse(xhr.responseText);
                setError(response.message || 'Upload failed');
              } catch {
                setError('Upload failed');
              }
              resolve(false);
            }
          };

          xhr.onerror = () => {
            setLoading(false);
            setError('Network error during upload');
            resolve(false);
          };

          xhr.open('POST', `/api/service/file/upload/${complaintId}`);
          if (mailOverride) {
            xhr.setRequestHeader(OVERRIDE_HEADER, mailOverride);
          }
          xhr.send(formData);
        });
      } catch (err) {
        setLoading(false);
        setError(err instanceof Error ? err.message : 'Upload failed');
        return false;
      }
    },
    []
  );

  return { upload, loading, error, progress, reset };
}

// Bulk Upload Hook for multiple files
interface UploadResult {
  file: File;
  success: boolean;
  error?: string;
}

interface UseMultiFileUploadReturn {
  uploadAll: (
    complaintId: number,
    files: File[],
    customerNr: string
  ) => Promise<UploadResult[]>;
  results: UploadResult[];
  isUploading: boolean;
  reset: () => void;
}

export function useMultiFileUpload(): UseMultiFileUploadReturn {
  const [results, setResults] = useState<UploadResult[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const reset = useCallback(() => {
    setResults([]);
    setIsUploading(false);
  }, []);

  const uploadAll = useCallback(
    async (
      complaintId: number,
      files: File[],
      customerNr: string
    ): Promise<UploadResult[]> => {
      setIsUploading(true);
      setResults([]);

      const uploadResults: UploadResult[] = [];

      // Sequential upload to avoid overwhelming the backend
      for (const file of files) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('customerNr', customerNr);

          const response = await serviceFetch(
            `/api/service/file/upload/${complaintId}`,
            {
              method: 'POST',
              body: formData,
            }
          );

          const data = await response.json();

          if (response.ok) {
            uploadResults.push({ file, success: true });
          } else {
            uploadResults.push({ file, success: false, error: data.message });
          }
        } catch (err) {
          uploadResults.push({
            file,
            success: false,
            error: err instanceof Error ? err.message : 'Upload failed',
          });
        }

        setResults([...uploadResults]);
      }

      setIsUploading(false);
      return uploadResults;
    },
    []
  );

  return { uploadAll, results, isUploading, reset };
}
